/**
 * Shared helpers for the admin Messages tab: status / recipient-kind / origin /
 * channel metadata and humanisers, the bucket (Scheduled | Sent | History)
 * definitions, and the filter object ⇄ URLSearchParams ⇄ API params mapping
 * that keeps every view shareable through the query string.
 *
 * Date / count formatters are shared with the Automations tab — import them
 * from ../automations/automationUtils (formatRelativeTime, formatDateTime,
 * formatCount, formatEmailVariant).
 */

// ---------------------------------------------------------------------------
// Statuses
// ---------------------------------------------------------------------------

export const MESSAGE_STATUSES = ["scheduled", "sending", "sent", "failed", "suppressed", "cancelled"];

/** `tone` maps 1:1 onto the design-system StatusBadge variants. */
export const MESSAGE_STATUS_META = {
  scheduled: {
    label: "Scheduled",
    tone: "info",
    description: "Waiting for its scheduled time — it is sent no earlier than that, by the next delivery cycle.",
  },
  sending: {
    label: "Sending",
    tone: "info",
    description: "Picked up by the delivery routine right now.",
  },
  sent: {
    label: "Sent",
    tone: "success",
    description: "Delivered (inbox) or handed to the email provider.",
  },
  failed: {
    label: "Failed",
    tone: "danger",
    description: "Delivery failed — see the error for details.",
  },
  suppressed: {
    label: "Suppressed",
    tone: "warning",
    description: "Held back at send time: suppressed address or the recipient opted out of this kind of email.",
  },
  cancelled: {
    label: "Cancelled",
    tone: "neutral",
    description: "Cancelled by an admin before it was sent.",
  },
};

export const getMessageStatusMeta = (status) =>
  MESSAGE_STATUS_META[status] || { label: status || "Unknown", tone: "neutral", description: "" };

// ---------------------------------------------------------------------------
// Recipient kinds (snapshot at enqueue time)
// ---------------------------------------------------------------------------

export const RECIPIENT_KINDS = ["user", "affiliate", "partner", "external"];

export const RECIPIENT_KIND_META = {
  user: { label: "User", description: "A platform user at the time of the send." },
  affiliate: { label: "Affiliate", description: "An affiliate account at the time of the send." },
  partner: { label: "Partner", description: "A partner account at the time of the send." },
  external: { label: "External", description: "An address with no account (gift recipients, admin notices…)." },
};

export const getRecipientKindMeta = (kind) =>
  RECIPIENT_KIND_META[kind] || { label: humanise(kind) || "Unknown", description: "" };

/** `recipient_was_paid` snapshot → "Paid" | "Free" | null (unknown / not a user). */
export const formatPaidAtSend = (value) => {
  if (value === true) return "Paid";
  if (value === false) return "Free";
  return null;
};

// ---------------------------------------------------------------------------
// Origins
// ---------------------------------------------------------------------------

export const ORIGINS = ["automation", "broadcast", "system"];

export const ORIGIN_META = {
  automation: { label: "Automation", description: "Enqueued by a scheduled, condition-driven automation." },
  broadcast: { label: "Broadcast", description: "Sent manually from the Broadcasts tab." },
  system: { label: "System", description: "Transactional email sent by the platform itself." },
};

export const getOriginMeta = (origin) =>
  ORIGIN_META[origin] || { label: humanise(origin) || "Unknown", description: "" };

/** Known machine-readable origin_detail values → human labels. Anything else is humanised. */
export const ORIGIN_DETAIL_LABELS = {
  verification_code: "Verification code",
  password_recovery: "Password recovery",
  coupon_gift: "Coupon gift",
  generation_complete: "Website generation complete",
  account_deletion_requested: "Account deletion requested",
  account_deletion_cancelled: "Account deletion cancelled",
  account_deletion_completed: "Account deletion completed",
  account_deletion_admin_notice: "Account deletion admin notice",
  affiliate_credentials: "Affiliate credentials",
  affiliate_welcome: "Affiliate welcome",
  admin_broadcast: "Admin broadcast",
};

/** "verification_code" → "Verification code". */
export function humanise(value) {
  const text = String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export const formatOriginDetail = (detail) => ORIGIN_DETAIL_LABELS[detail] || humanise(detail);

// ---------------------------------------------------------------------------
// Channels
// ---------------------------------------------------------------------------

export const CHANNELS = ["inbox", "email"];

export const CHANNEL_META = {
  inbox: { label: "Inbox", description: "In-app inbox message." },
  email: { label: "Email", description: "Email, rendered with the template variant shown." },
};

export const getChannelMeta = (channel) =>
  CHANNEL_META[channel] || { label: humanise(channel) || "Unknown", description: "" };

// ---------------------------------------------------------------------------
// Suppression list reasons
// ---------------------------------------------------------------------------

export const SUPPRESSION_REASON_META = {
  bounce: { label: "Bounce", tone: "danger", description: "The provider reported a hard bounce for this address." },
  complaint: { label: "Complaint", tone: "warning", description: "The recipient marked our email as spam." },
  manual: { label: "Manual", tone: "info", description: "Added by an admin." },
};

export const getSuppressionReasonMeta = (reason) =>
  SUPPRESSION_REASON_META[reason] || { label: humanise(reason) || "Unknown", tone: "neutral", description: "" };

// ---------------------------------------------------------------------------
// Buckets (the Scheduled | Sent | History views)
// ---------------------------------------------------------------------------

export const BUCKETS = ["scheduled", "sent", "history"];
export const DEFAULT_BUCKET = "scheduled";

export const BUCKET_META = {
  scheduled: {
    label: "Scheduled",
    title: "Scheduled messages",
    description: "Waiting to be sent — each row goes out no earlier than its scheduled time. Scheduled rows can be cancelled.",
    whenLabel: "Scheduled for",
    dateFilterLabel: "Scheduled",
    emptyTitle: "No scheduled messages",
    emptyDescription:
      "Nothing is waiting to be sent. Active automations and broadcasts enqueue messages here; each one is then delivered no earlier than its scheduled time.",
    statuses: ["scheduled", "sending"],
  },
  sent: {
    label: "Sent",
    title: "Sent messages",
    description: "Inbox messages and emails that were delivered, newest first.",
    whenLabel: "Sent",
    dateFilterLabel: "Created",
    emptyTitle: "No sent messages yet",
    emptyDescription: "Delivered inbox messages and emails appear here as soon as the delivery routine sends them.",
    statuses: ["sent"],
  },
  history: {
    label: "History",
    title: "Message history",
    description: "Everything that is no longer pending: sent, failed, suppressed and cancelled messages.",
    whenLabel: "When",
    dateFilterLabel: "Created",
    emptyTitle: "No message history",
    emptyDescription: "Sent, failed, suppressed and cancelled messages appear here once a message leaves the scheduled state.",
    statuses: ["sent", "failed", "suppressed", "cancelled"],
  },
};

export const getBucketMeta = (bucket) => BUCKET_META[bucket] || BUCKET_META[DEFAULT_BUCKET];

/** Per-bucket totals from the API `counts` (scheduled tab = scheduled + sending). */
export const bucketCounts = (counts) => {
  const c = counts || {};
  const n = (key) => Number(c[key] || 0);
  return {
    scheduled: n("scheduled") + n("sending"),
    sent: n("sent"),
    history: n("sent") + n("failed") + n("suppressed") + n("cancelled"),
  };
};

/** Most relevant timestamp of a row for the History view: sent_at for sent rows, updated_at otherwise. */
export const historyTimestamp = (row) => {
  if (!row) return { label: "Updated", value: null };
  if (row.status === "sent") return { label: "Sent", value: row.sent_at || row.updated_at || null };
  const meta = getMessageStatusMeta(row.status);
  return { label: meta.label, value: row.updated_at || row.created_at || null };
};

export const isCancellable = (row) => Boolean(row) && row.status === "scheduled";

/** Display name + email for the Recipient column. */
export const describeRecipient = (row) => ({
  name: row?.user?.username || (row?.recipient_kind === "external" ? "External recipient" : "") || "",
  email: row?.recipient_email || row?.user?.email || "",
});

// ---------------------------------------------------------------------------
// Filters ⇄ URL ⇄ API
// ---------------------------------------------------------------------------

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
export const DEFAULT_PAGE_SIZE = 50;

export const ORDERING_VALUES = ["-created_at", "created_at", "scheduled_for", "-scheduled_for", "-sent_at"];

export const ORDERING_OPTIONS = [
  { value: "-created_at", label: "Newest created first" },
  { value: "created_at", label: "Oldest created first" },
  { value: "scheduled_for", label: "Scheduled: soonest first" },
  { value: "-scheduled_for", label: "Scheduled: latest first" },
  { value: "-sent_at", label: "Most recently sent first" },
];

/** Label of the API default ordering for a bucket (used for the "" option). */
export const defaultOrderingLabel = (bucket) =>
  bucket === "scheduled" ? "Default (scheduled: soonest first)" : "Default (newest created first)";

export const DEFAULT_FILTERS = Object.freeze({
  bucket: DEFAULT_BUCKET,
  q: "",
  recipient_kind: "",
  recipient_was_paid: "",
  origin: "",
  origin_detail: "",
  automation_id: "",
  channel: "",
  email_template_variant: "",
  status: [],
  created_from: "",
  created_to: "",
  scheduled_from: "",
  scheduled_to: "",
  ordering: "",
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
});

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const POSITIVE_INT_RE = /^\d+$/;

const asString = (value) => String(value ?? "").trim();

const oneOf = (value, allowed, fallback = "") => (allowed.includes(value) ? value : fallback);

const asDate = (value) => (DATE_RE.test(asString(value)) ? asString(value) : "");

const asPositiveInt = (value, fallback) => {
  const text = asString(value);
  if (!POSITIVE_INT_RE.test(text)) return fallback;
  const n = Number(text);
  return n >= 1 ? n : fallback;
};

const asStatusList = (value) => {
  const raw = Array.isArray(value) ? value.join(",") : asString(value);
  return Array.from(new Set(raw.split(",").map((s) => s.trim()).filter((s) => MESSAGE_STATUSES.includes(s))));
};

/**
 * Coerce any partial filter object into a complete, self-consistent one:
 * unknown choice values fall back to "", dependent filters are dropped when
 * their parent does not apply (paid → kind, automation → origin=automation,
 * system kind → origin=system, status chips → History, scheduled dates →
 * Scheduled bucket, created dates → the other buckets).
 */
export const normalizeFilters = (input = {}) => {
  const f = { ...DEFAULT_FILTERS, ...(input || {}) };
  const bucket = oneOf(asString(f.bucket), BUCKETS, DEFAULT_BUCKET);
  const recipient_kind = oneOf(asString(f.recipient_kind), RECIPIENT_KINDS);
  const origin = oneOf(asString(f.origin), ORIGINS);
  const paidAllowed = recipient_kind === "" || recipient_kind === "user";
  const pageSize = Number(f.page_size);
  return {
    bucket,
    q: asString(f.q).slice(0, 200),
    recipient_kind,
    recipient_was_paid: paidAllowed ? oneOf(asString(f.recipient_was_paid), ["true", "false"]) : "",
    origin,
    origin_detail: origin === "system" ? asString(f.origin_detail).slice(0, 60) : "",
    automation_id: origin === "automation" && POSITIVE_INT_RE.test(asString(f.automation_id)) ? asString(f.automation_id) : "",
    channel: oneOf(asString(f.channel), CHANNELS),
    email_template_variant: asString(f.email_template_variant).slice(0, 40),
    status: bucket === "history" ? asStatusList(f.status) : [],
    created_from: bucket === "scheduled" ? "" : asDate(f.created_from),
    created_to: bucket === "scheduled" ? "" : asDate(f.created_to),
    scheduled_from: bucket === "scheduled" ? asDate(f.scheduled_from) : "",
    scheduled_to: bucket === "scheduled" ? asDate(f.scheduled_to) : "",
    ordering: oneOf(asString(f.ordering), ORDERING_VALUES),
    page: asPositiveInt(f.page, 1),
    page_size: PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
  };
};

const URL_KEYS = [
  "bucket",
  "q",
  "recipient_kind",
  "recipient_was_paid",
  "origin",
  "origin_detail",
  "automation_id",
  "channel",
  "email_template_variant",
  "status",
  "created_from",
  "created_to",
  "scheduled_from",
  "scheduled_to",
  "ordering",
  "page",
  "page_size",
];

/** URLSearchParams (react-router) → normalised filter object. */
export const parseFiltersFromSearchParams = (searchParams) => {
  const raw = {};
  URL_KEYS.forEach((key) => {
    const values = typeof searchParams?.getAll === "function" ? searchParams.getAll(key) : [];
    if (values.length === 0) return;
    raw[key] = key === "status" ? values.join(",") : values[values.length - 1];
  });
  return normalizeFilters(raw);
};

/** Normalised filters → URLSearchParams with defaults omitted (short, shareable URLs). */
export const filtersToSearchParams = (filters) => {
  const f = normalizeFilters(filters);
  const params = new URLSearchParams();
  if (f.bucket !== DEFAULT_BUCKET) params.set("bucket", f.bucket);
  [
    "q",
    "recipient_kind",
    "recipient_was_paid",
    "origin",
    "origin_detail",
    "automation_id",
    "channel",
    "email_template_variant",
    "created_from",
    "created_to",
    "scheduled_from",
    "scheduled_to",
    "ordering",
  ].forEach((key) => {
    if (f[key]) params.set(key, f[key]);
  });
  if (f.status.length > 0) params.set("status", f.status.join(","));
  if (f.page > 1) params.set("page", String(f.page));
  if (f.page_size !== DEFAULT_PAGE_SIZE) params.set("page_size", String(f.page_size));
  return params;
};

/** Normalised filters → params object for adminMessagesList (empties are skipped by the API helper). */
export const filtersToApiParams = (filters) => {
  const f = normalizeFilters(filters);
  return {
    page: f.page,
    page_size: f.page_size,
    bucket: f.bucket,
    status: f.status,
    q: f.q,
    recipient_kind: f.recipient_kind,
    recipient_was_paid: f.recipient_was_paid,
    origin: f.origin,
    origin_detail: f.origin_detail,
    automation_id: f.automation_id,
    channel: f.channel,
    email_template_variant: f.email_template_variant,
    created_from: f.created_from,
    created_to: f.created_to,
    scheduled_from: f.scheduled_from,
    scheduled_to: f.scheduled_to,
    ordering: f.ordering,
  };
};

const ACTIVE_FILTER_KEYS = [
  "q",
  "recipient_kind",
  "recipient_was_paid",
  "origin",
  "origin_detail",
  "automation_id",
  "channel",
  "email_template_variant",
  "created_from",
  "created_to",
  "scheduled_from",
  "scheduled_to",
];

/** How many narrowing filters are set (bucket, ordering, page and page size do not count). */
export const countActiveFilters = (filters) => {
  const f = normalizeFilters(filters);
  let n = ACTIVE_FILTER_KEYS.filter((key) => Boolean(f[key])).length;
  if (f.status.length > 0) n += 1;
  return n;
};

/** "Clear filters": keep the bucket and page size, reset everything else. */
export const clearedFilters = (filters) => {
  const f = normalizeFilters(filters);
  return normalizeFilters({ ...DEFAULT_FILTERS, bucket: f.bucket, page_size: f.page_size });
};

/** Switch bucket: page 1, bucket-specific filters (status chips, dates, ordering) reset. */
export const filtersForBucket = (filters, bucket) => {
  const f = normalizeFilters(filters);
  return normalizeFilters({
    ...f,
    bucket,
    status: [],
    created_from: "",
    created_to: "",
    scheduled_from: "",
    scheduled_to: "",
    ordering: "",
    page: 1,
  });
};

/** Shareable URL (path + query) for the Messages tab with the given filters applied. */
export const messagesPathFor = (filters, basePath = "/admin/dashboard/messages") => {
  const qs = filtersToSearchParams(filters).toString();
  return qs ? `${basePath}?${qs}` : basePath;
};
